import { Brain, Globe, MessagesSquare, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  AI_MODEL,
  SEARCH_SUPPORTED_MODELS,
} from "@/store/zustand/aiActionStore";

type FeatureLabel = string | ((model: AI_MODEL) => string);

const featureConfig = {
  reasoning: {
    icon: Brain,
    label: "Reasoning with AI" as FeatureLabel,
  },
  search: {
    icon: Globe,
    label: ((model: AI_MODEL) =>
      !SEARCH_SUPPORTED_MODELS.has(model)
        ? "Search is only supported with Gemini"
        : "Search with Gemini") as FeatureLabel,
  },
  chatOnly: {
    icon: MessagesSquare,
    label: "Chat Only Mode" as FeatureLabel,
  },
};

const ActiveFeatureTags = ({
  useReasoning,
  useSearch,
  useChatOnly,
  setUseReasoning,
  setUseSearch,
  setUseChatOnly,
  isChatLoading,
  reasoningModel,
}: {
  useReasoning: boolean;
  useSearch: boolean;
  useChatOnly: boolean;
  setUseReasoning: (val: boolean) => void;
  setUseSearch: (val: boolean) => void;
  setUseChatOnly: (val: boolean) => void;
  isChatLoading: boolean;
  reasoningModel: AI_MODEL;
}) => {
  const [isActive, setIsActive] = useState("");
  const tags = [
    {
      label: "Reasoning with AI",
      key: "reasoning",
      active: useReasoning,
      toggle: () => setUseReasoning(false),
    },
    {
      label: "Web Search",
      key: "search",
      active: useSearch,
      toggle: () => setUseSearch(false),
    },
    {
      label: "Chat Only Mode",
      key: "chatOnly",
      active: useChatOnly,
      toggle: () => setUseChatOnly(false),
    },
  ];

  return (
    <div className="flex items-center justify-start gap-1">
      {tags.map(({ key, active, toggle }) => {
        if (!active) return null;

        const Icon = featureConfig[key as keyof typeof featureConfig].icon;
        const labelConfig =
          featureConfig[key as keyof typeof featureConfig].label;
        const label =
          typeof labelConfig === "function"
            ? labelConfig(reasoningModel)
            : labelConfig;

        return (
          <>
            <Button
              onMouseOver={() => {
                setIsActive(key);
              }}
              onMouseLeave={() => {
                setIsActive("");
              }}
              data-tooltip-id="tooltip"
              data-tooltip-content={label}
              data-tooltip-place="top"
              variant="secondary"
              size="icon"
              className={cn(
                "w-full h-7 px-[6px] rounded-md gap-1 group bg-[#6448EF33] hover:bg-primary-n-600",
                isChatLoading && "opacity-50 cursor-not-allowed"
              )}
            >
              <Icon className="w-4 h-4 text-primary-n-600 group-hover:text-secondary-n-50" />
              {isActive === key && (
                <X
                  className="w-3.5 h-3.5 cursor-pointer text-primary-n-600 group-hover:text-secondary-n-50"
                  onClick={() => !isChatLoading && toggle()}
                />
              )}
            </Button>
          </>
        );
      })}
    </div>
  );
};

export default ActiveFeatureTags;
